/**
 * Declarative config of the shared claims table (`ClaimsTableCard`): the row
 * view model, the stable columns and the DataList config builder.
 */

import type { ColumnConfig, DataListConfig } from '@components/integrated/data-list';

import { EMPTY_STATE_TEXT } from './cardText';
import {
  EXTRANET_DATE_RENDERER,
  EXTRANET_ROW_OPEN_HANDLER,
  type ExtranetRowContext,
} from './cellRenderers';

/** One claim row (view model mapped by the domain service). */
export interface ClaimTableRow {
  /** Stable row id (`ID_SIN`). */
  id: string;
  /** Claim reference (`NCBT_SIN`). */
  reference: string | null;
  /** Circumstances (`LIB_CIRC`). */
  circonstances: string | null;
  /** Occurrence date: ms timestamp (`DATS_SIN`) or pre-formatted string. */
  dateSurvenance: number | string | null;
  /** Insurer cost in euros (`COUT_ESTIME` / `COUT_TOTAL_ASSUREUR`). */
  coutAssureur: number | null;
  /** Absolute path opened on row click (claim detail); omitted ⇒ not clickable. */
  to?: string | null;
}

/** Stable column keys. */
export type ClaimsTableColumnKey = 'reference' | 'circonstances' | 'dateSurvenance' | 'coutAssureur';

/** Base columns (design order). */
export const CLAIMS_TABLE_COLUMNS: Readonly<Record<ClaimsTableColumnKey, ColumnConfig>> = {
  reference: {
    label: 'Référence',
    order: 1,
    type: 'text',
    className: 'font-mono text-xs',
    sortable: true,
  },
  circonstances: {
    label: 'Circonstances',
    order: 2,
    type: 'text',
    sortable: true,
  },
  dateSurvenance: {
    label: 'Survenance',
    headerTooltip: 'Date de survenance du sinistre',
    order: 3,
    type: 'custom',
    typeOptions: { renderer: EXTRANET_DATE_RENDERER },
    sortable: true,
  },
  coutAssureur: {
    label: 'Coût',
    headerTooltip: "Coût pour l'assureur",
    order: 4,
    type: 'currency',
    typeOptions: { currency: 'EUR', locale: 'fr-FR' },
    align: 'right',
    sortable: true,
  },
};

/** A table row carrying its per-row context (`__ctx.onOpen`). */
export type ClaimTableListRow = ClaimTableRow & { __ctx?: ExtranetRowContext };

/**
 * Base DataList config of the claims table: inline rows in API order, every
 * row on one page, no toolbar; rows are clickable when at least one has a
 * `to` path.
 */
export function buildClaimsTableConfig(
  componentId: string,
  rows: readonly ClaimTableListRow[],
): DataListConfig {
  const clickable = rows.some((row) => Boolean(row.to));
  return {
    id: componentId,
    dataSource: { data: rows as unknown as Record<string, unknown>[] },
    columns: { ...CLAIMS_TABLE_COLUMNS },
    display: { size: 'compact', hoverable: clickable },
    sorting: { enabled: true },
    toolbar: { visible: false },
    pagination: { mode: 'scroll', pageSize: 999_999 },
    ...(clickable ? { actions: { rowClick: { handler: EXTRANET_ROW_OPEN_HANDLER } } } : {}),
    i18n: { emptyMessage: EMPTY_STATE_TEXT },
  };
}
